import React, { useState } from "react";
import {
  Link,
  useLocation,
} from "react-router-dom";
import { resetPassword } from "../services/authApi";

const ResetPasswordLayer = () => {
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] =
    useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] =
    useState(false);
  const [isComplete, setIsComplete] = useState(false);

  const handlePasswordChange = (event) => {
    setPassword(event.target.value);

    if (error) {
      setError("");
    }
  };

  const handleConfirmPasswordChange = (event) => {
    setConfirmPassword(event.target.value);

    if (error) {
      setError("");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isSubmitting || isComplete) {
      return;
    }

    setError("");

    if (!token || token.length < 20) {
      setError(
        "This password reset link is invalid or has expired."
      );
      return;
    }

    if (password.length < 12 || password.length > 128) {
      setError(
        "Password must be between 12 and 128 characters."
      );
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setIsSubmitting(true);

    try {
      await resetPassword({
        token,
        password,
      });

      setPassword("");
      setConfirmPassword("");
      setIsComplete(true);
    } catch (requestError) {
      if (requestError.code === "INVALID_RESET_TOKEN") {
        setError(
          "This password reset link is invalid or has expired."
        );
        return;
      }

      if (requestError.code === "INVALID_PASSWORD") {
        setError(
          requestError.message ||
            "Please choose a valid password."
        );
        return;
      }

      setError(
        requestError.message ||
          "We could not reset your password. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isComplete) {
    return (
      <div className="simplif-auth-page">
        <header className="simplif-auth-header">
          <Link to="/sign-in" className="simplif-auth-logo">
            <img
              src="/assets/images/simplif/simplif-logo.png"
              alt="Simplif"
            />
          </Link>

          <div className="simplif-auth-header-actions">
            <Link
              to="/sign-in"
              className="simplif-auth-login-link"
            >
              Login
            </Link>

            <Link
              to="/sign-up"
              className="simplif-auth-signup-button"
            >
              SIGN UP
            </Link>
          </div>
        </header>

        <main className="simplif-auth-main">
          <div className="simplif-auth-card">
            <section className="simplif-auth-form-panel">
              <div className="simplif-auth-form-content">
                <h1>Password Reset Complete</h1>

                <p
                  className="simplif-auth-subtitle"
                  role="status"
                >
                  Your password has been changed successfully.
                  You can now sign in with your new password.
                </p>

                <Link
                  to="/sign-in"
                  className="simplif-auth-primary-button"
                >
                  Sign in
                </Link>
              </div>
            </section>

            <section className="simplif-auth-side-panel">
              <div className="simplif-auth-pattern simplif-auth-pattern-top" />

              <div className="simplif-auth-side-content">
                <h2>Return to Sign in</h2>

                <p>
                  Your password is ready. Sign in to access your
                  Simplif dashboard.
                </p>

                <Link
                  to="/sign-in"
                  className="simplif-auth-secondary-button"
                >
                  Sign in
                </Link>
              </div>

              <div className="simplif-auth-pattern simplif-auth-pattern-bottom" />

              <div className="simplif-auth-support">
                <span className="simplif-auth-support-icon">
                  ?
                </span>
                <strong>Need Help?</strong> Support@simplif.com
              </div>
            </section>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="simplif-auth-page">
      <header className="simplif-auth-header">
        <Link to="/sign-in" className="simplif-auth-logo">
          <img
            src="/assets/images/simplif/simplif-logo.png"
            alt="Simplif"
          />
        </Link>

        <div className="simplif-auth-header-actions">
          <Link
            to="/sign-in"
            className="simplif-auth-login-link"
          >
            Login
          </Link>

          <Link
            to="/sign-up"
            className="simplif-auth-signup-button"
          >
            SIGN UP
          </Link>
        </div>
      </header>

      <main className="simplif-auth-main">
        <div className="simplif-auth-card">
          <section className="simplif-auth-form-panel">
            <div className="simplif-auth-form-content">
              <h1>Choose a New Password</h1>

              {!token || token.length < 20 ? (
                <>
                  <p
                    className="simplif-auth-status-message"
                    role="alert"
                  >
                    This password reset link is invalid or has
                    expired.
                  </p>

                  <div className="simplif-auth-bottom-link">
                    <Link to="/forgot-password">
                      Request a new reset link
                    </Link>
                  </div>
                </>
              ) : (
                <>
                  <p className="simplif-auth-subtitle">
                    Enter a new password for your Simplif
                    account.
                  </p>

                  <form onSubmit={handleSubmit}>
                    <div className="simplif-auth-field">
                      <label htmlFor="new-password">
                        New Password
                      </label>

                      <input
                        id="new-password"
                        type="password"
                        placeholder="New Password"
                        autoComplete="new-password"
                        value={password}
                        onChange={handlePasswordChange}
                        minLength={12}
                        maxLength={128}
                        disabled={isSubmitting}
                        required
                      />
                    </div>

                    <div className="simplif-auth-field">
                      <label htmlFor="confirm-new-password">
                        Confirm New Password
                      </label>

                      <input
                        id="confirm-new-password"
                        type="password"
                        placeholder="Confirm New Password"
                        autoComplete="new-password"
                        value={confirmPassword}
                        onChange={handleConfirmPasswordChange}
                        minLength={12}
                        maxLength={128}
                        disabled={isSubmitting}
                        required
                      />
                    </div>

                    {error ? (
                      <p
                        className="simplif-auth-status-message"
                        role="alert"
                      >
                        {error}
                      </p>
                    ) : null}

                    <button
                      type="submit"
                      className="simplif-auth-primary-button"
                      disabled={isSubmitting}
                    >
                      {isSubmitting
                        ? "Resetting..."
                        : "Reset Password"}
                    </button>
                  </form>
                </>
              )}
            </div>
          </section>

          <section className="simplif-auth-side-panel">
            <div className="simplif-auth-pattern simplif-auth-pattern-top" />

            <div className="simplif-auth-side-content">
              <h2>Return to Sign in</h2>

              <p>
                Remembered your password? Return to sign in.
              </p>

              <Link
                to="/sign-in"
                className="simplif-auth-secondary-button"
              >
                Sign in
              </Link>
            </div>

            <div className="simplif-auth-pattern simplif-auth-pattern-bottom" />

            <div className="simplif-auth-support">
              <span className="simplif-auth-support-icon">?</span>
              <strong>Need Help?</strong> Support@simplif.com
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default ResetPasswordLayer;
